import React, { useState } from "react";
import { PageItem, PaginationWrapper } from "./style";

const LEFT_PAGE = "LEFT";
const RIGHT_PAGE = "RIGHT";
const FIRST_PAGE = "FIRST";
const LAST_PAGE = "LAST";

const Pagination = (props) => {
	let { totalRecords = null, pageLimit = 30, pageNeighbours = 2 } = props;
	const [currentPage, setCurrentPage] = useState(1);

	// pageNeighbours can be: 0, 1 or 2
	pageNeighbours =
		typeof pageNeighbours === "number"
			? Math.max(0, Math.min(pageNeighbours, 2))
			: 0;

	let totalPages = Math.ceil(totalRecords / pageLimit);

	/**
	 * Helper method for creating a range of numbers
	 * range(1, 5) => [1, 2, 3, 4, 5]
	 */
	const range = (from, to, step = 1) => {
		let i = from;
		const range = [];

		while (i <= to) {
			range.push(i);
			i += step;
		}

		return range;
	};

	const fetchPageNumbers = () => {
		/**
		 * totalNumbers: the total page numbers to show on the control
		 */
		const totalNumbers = pageNeighbours * 2 + 1;

		if (totalPages > totalNumbers) {
			const startPage = Math.max(1, currentPage - pageNeighbours);
			const endPage = Math.min(totalPages, currentPage + pageNeighbours);
			let pages = range(startPage, endPage);

			/**
			 * hasLeftSpill: has hidden pages to the left
			 * hasRightSpill: has hidden pages to the right
			 */
			const hasLeftSpill = startPage > 1;
			const hasRightSpill = endPage < totalPages;

			switch (true) {
				case hasLeftSpill && !hasRightSpill && currentPage !== totalPages: {
					pages = [FIRST_PAGE, LEFT_PAGE, ...pages, RIGHT_PAGE];
					break;
				}

				case hasLeftSpill && !hasRightSpill: {
					pages = [FIRST_PAGE, LEFT_PAGE, ...pages];
					break;
				}

				case !hasLeftSpill &&
					hasRightSpill &&
					pages[0] === 1 &&
					currentPage !== 1: {
					pages = [LEFT_PAGE, ...pages, RIGHT_PAGE, LAST_PAGE];
					break;
				}

				case !hasLeftSpill && hasRightSpill: {
					pages = [...pages, RIGHT_PAGE, LAST_PAGE];
					break;
				}

				default: {
					pages = [FIRST_PAGE, LEFT_PAGE, ...pages, RIGHT_PAGE, LAST_PAGE];
					break;
				}
			}

			return [...pages];
		}

		return range(1, totalPages);
	};

	const goToPage = (page) => {
		const { onPageChanged = (f) => f } = props;
		const currentPage = Math.max(0, Math.min(page, totalPages));
		const paginationData = {
			currentPage,
			totalPages: totalPages,
			pageLimit: pageLimit,
			totalRecords: totalRecords,
		};

		setCurrentPage(currentPage);
		onPageChanged(paginationData);
	};

	const handleClick = (page) => (evt) => {
		evt.preventDefault();
		goToPage(page);
	};

	const previous = (evt) => {
		evt.preventDefault();
		goToPage(currentPage - 1);
	};

	const next = (evt) => {
		evt.preventDefault();
		goToPage(currentPage + 1);
	};

	if (!totalRecords || totalPages === 1) return null;

	const pages = fetchPageNumbers();

	return (
		<nav>
			<PaginationWrapper className="pagination">
				{pages.map((page, index) => {
					if (page === LEFT_PAGE)
						return (
							<PageItem key={index} className="page-item bold">
								<a
									className="page-link"
									href="#"
									aria-label="Previous"
									onClick={previous}
								>
									<span aria-hidden="true">&lt;</span>
								</a>
							</PageItem>
						);

					if (page === RIGHT_PAGE)
						return (
							<PageItem key={index} className="page-item bold">
								<a
									className="page-link"
									href="#"
									aria-label="Next"
									onClick={next}
								>
									<span aria-hidden="true">&gt;</span>
								</a>
							</PageItem>
						);

					if (page === LAST_PAGE)
						return (
							<PageItem key={index} className="page-item bold">
								<a
									className="page-link"
									href="#"
									aria-label="Next"
									onClick={handleClick(totalPages)}
								>
									<span aria-hidden="true">&gt;&gt;</span>
								</a>
							</PageItem>
						);

					if (page === FIRST_PAGE)
						return (
							<PageItem key={index} className="page-item bold">
								<a
									className="page-link"
									href="#"
									aria-label="Next"
									onClick={handleClick(1)}
								>
									<span aria-hidden="true">&lt;&lt;</span>
								</a>
							</PageItem>
						);

					return (
						<PageItem
							key={index}
							className={`page-item${currentPage === page ? " active" : ""}`}
						>
							<a className="page-link" href="#" onClick={handleClick(page)}>
								{page}
							</a>
						</PageItem>
					);
				})}
			</PaginationWrapper>
		</nav>
	);
};

export default Pagination;
